import React from 'react';
import { 
  FileText, 
  Printer, 
  X,
  GraduationCap,
  Award,
  User,
  School,
  Calendar
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

/**
 * Primary School Report Card Component
 * Based on Quatui Primary School format
 * Includes: English, Maths, General subjects with Official Vanuatu A-E Scale
 * Auto-generated teacher and principal comments
 */
export default function PrimaryReportCard({ 
  isOpen, 
  onClose, 
  student, 
  classInfo, 
  assessmentMarks,
  reportData, // NEW: Database report data with all subjects
  schoolName = "Quatui Primary School",
  schoolAddress = "Longana East Ambae",
  schoolPhone = "7783728",
  schoolId = "", // School ID for logo
  teacherName = "Class Teacher",
  principalName = "Principal",
  term = "Term 1",
  year = 2026,
  absences = 0
}) {
  if (!student) return null;

  // Check if school is Ambaebulu (show logo)
  const isAmbaebulu = schoolId?.includes('ambaebulu') || 
                      schoolName?.toLowerCase().includes('ambaebulu') ||
                      schoolName?.toLowerCase().includes('ecole primaire francophone');
  
  // Check if school is Quatui
  const isQuatui = schoolId?.includes('quatui') || 
                   schoolName?.toLowerCase().includes('quatui');
  
  // Check if school is Loquirutaro
  const isLoquirutaro = schoolId?.includes('quirutaro') || 
                        schoolName?.toLowerCase().includes('quirutaro');
  
  // Check if school is St Patrick's (Junior or Senior)
  const isStPatricks = schoolId?.includes('stpatricks') || 
                       schoolId?.includes('stpatrick') ||
                       schoolName?.toLowerCase().includes('st patrick') ||
                       schoolName?.toLowerCase().includes('st. patrick');
  
  // Set logo based on school
  let logoUrl = null;
  if (isAmbaebulu) {
    logoUrl = '/ambaebulu-logo.png';
  } else if (isQuatui) {
    logoUrl = '/quatui-logo.png';
  } else if (isLoquirutaro) {
    logoUrl = '/loquirutaro-logo.png';
  } else if (isStPatricks) {
    logoUrl = '/stpatricks-logo.png';
  }

  // Official Vanuatu A-E Grading Scale (Primary Years 1-6)
  // Official Vanuatu A-E Grading Scale (Primary Years 1-6)
  const calculateGrade = (percentage) => {
    if (percentage >= 85) return { grade: 'A', gpa: 4.0, description: 'Excellent' };
    if (percentage >= 70) return { grade: 'B', gpa: 3.0, description: 'Good' };
    if (percentage >= 50) return { grade: 'C', gpa: 2.0, description: 'Satisfactory' };
    if (percentage >= 30) return { grade: 'D', gpa: 1.0, description: 'Limited' };
    return { grade: 'E', gpa: 0.0, description: 'Low' };
  };

  // Get marks for this student from legacy assessmentMarks (fallback)
  const getMarks = (type) => assessmentMarks?.[`${student.id}-${type}`] || 0;
  
  // Calculate totals for different components
  const quizMark = parseFloat(getMarks('quiz')) || 0;
  const homeworkMark = parseFloat(getMarks('homework')) || 0;
  const projectMark = parseFloat(getMarks('project')) || 0;
  const classworkMark = parseFloat(getMarks('classwork')) || 0;
  const examMark = parseFloat(getMarks('exam')) || 0;
  
  // CA is 40% of total, Exam is 60%
  const caTotal = Math.min(quizMark + homeworkMark + projectMark + classworkMark, 40);
  const examTotal = Math.min(examMark, 60);
  const totalMarks = caTotal + examTotal;
  const gradeInfo = calculateGrade(totalMarks);

  // Auto-generate subject-specific teacher comments based on performance
  const generateSubjectComment = (subjectName, percentage, grade, studentFirstName) => {
    // Comments aligned with Official Vanuatu A-E Scale
    const comments = {
      'A': [
        `${studentFirstName} demonstrates excellent understanding in ${subjectName}. Achievement is much higher than expected. Outstanding work!`,
        `Excellent achievement in ${subjectName}! ${studentFirstName} consistently demonstrates excellent understanding and enthusiasm.`,
        `${studentFirstName} has worked consistently well in ${subjectName}. Excellent progress this term.`
      ],
      'B': [
        `${studentFirstName} shows good understanding of ${subjectName} concepts. Achievement is higher than the standard expected.`,
        `Good performance in ${subjectName}. ${studentFirstName} demonstrates solid understanding of concepts.`,
        `${studentFirstName} has shown good effort in ${subjectName} this term. Keep it up!`
      ],
      'C': [
        `${studentFirstName} shows satisfactory understanding in ${subjectName}. Achievement is at the expected standard.`,
        `${studentFirstName} is making steady progress in ${subjectName}. Continue working consistently.`,
        `Satisfactory work in ${subjectName}. ${studentFirstName} meets the expected standard for this year level.`
      ],
      'D': [
        `${studentFirstName} demonstrates limited achievement in ${subjectName}. Performance is below what is expected.`,
        `${studentFirstName} is finding some ${subjectName} concepts challenging. Extra support recommended.`,
        `More effort needed in ${subjectName}. ${studentFirstName} should ask for help and practice daily.`
      ],
      'E': [
        `${studentFirstName} demonstrates low achievement in ${subjectName}. Performance is well below expected standards.`,
        `${studentFirstName} needs additional support in ${subjectName}. Parent-teacher meeting recommended.`,
        `${studentFirstName} is finding ${subjectName} very challenging. Extra tutoring and home support essential.`
      ]
    };
    
    const gradeComments = comments[grade] || comments['C'];
    // Deterministic selection based on subject name hash (consistent across renders)
    const hashCode = subjectName.split('').reduce((a, b) => ((a << 5) - a) + b.charCodeAt(0), 0);
    return gradeComments[Math.abs(hashCode) % gradeComments.length];
  };

  // Auto-generate overall teacher remarks
  const generateTeacherRemarks = (overallGrade, studentFirstName, absenceCount) => {
    let remark = '';
    
    // Performance-based remarks aligned with Official Vanuatu A-E Scale
    if (overallGrade === 'A') {
      remark = `${studentFirstName} has demonstrated excellent academic ability this term. Their achievement is much higher than the standard expected for this year level. ${studentFirstName} is a role model for other students.`;
    } else if (overallGrade === 'B') {
      remark = `${studentFirstName} has shown good progress this term. Their achievement is higher than the standard expected. With continued effort, ${studentFirstName} can achieve even better results.`;
    } else if (overallGrade === 'C') {
      remark = `${studentFirstName} has made satisfactory progress this term, meeting the expected standard for this year level. We encourage ${studentFirstName} to continue working consistently.`;
    } else if (overallGrade === 'D') {
      remark = `${studentFirstName} needs to improve their academic performance. More focus and effort in class, along with regular homework completion and study at home, will help. We recommend parents work closely with ${studentFirstName} on daily revision.`;
    } else {
      remark = `${studentFirstName} is facing significant challenges in their studies and requires additional support. We strongly recommend a parent-teacher meeting to discuss strategies for improvement. Daily practice and close supervision at home are essential.`;
    }
    
    // Add attendance remark if absences are significant
    if (absenceCount > 10) {
      remark += ` Note: ${studentFirstName} has had ${absenceCount} absences this term which may have affected their performance. Please ensure regular attendance next term.`;
    } else if (absenceCount > 5) {
      remark += ` Please note that ${studentFirstName} has had ${absenceCount} absences this term. Regular attendance is important for academic success.`;
    }
    
    return remark;
  };

  // Auto-generate principal remarks - Official Vanuatu A-E Scale
  const generatePrincipalRemarks = (overallGrade, studentFirstName, overallGpa) => {
    if (overallGrade === 'A') {
      return `Congratulations to ${studentFirstName} on excellent academic performance this term! Your achievement is much higher than the expected standard. Keep up the excellent work and continue to be an inspiration to your classmates. Well done!`;
    } else if (overallGrade === 'B') {
      return `Well done ${studentFirstName} on your good performance this term. Your achievement is higher than the standard expected. I encourage you to continue putting in effort and strive for even better results next term.`;
    } else if (overallGrade === 'C') {
      return `${studentFirstName}, you have shown satisfactory progress and are meeting the expected standard for this year level. I encourage you to continue working consistently to maintain and improve your results.`;
    } else if (overallGrade === 'D') {
      return `${studentFirstName}, your performance is below the expected standard. I encourage you to take your studies more seriously, complete all homework assignments, and seek help from your teachers. With proper support and effort, you can improve.`;
    } else {
      return `${studentFirstName}, your performance is well below the expected standard and we are concerned. Please work closely with your teachers and parents to improve. Attend all classes, complete your assignments, and don't hesitate to ask for help.`;
    }
  };

  // Primary subjects - use database data if available, otherwise fall back to legacy calculation
  const studentFirstName = student.name?.split(' ')[0] || 'Student';
  
  // Build subjects from database reportData if available
  let subjects = [];
  
  if (reportData?.subjects && reportData.subjects.length > 0) {
    // Use actual database data
    subjects = reportData.subjects.map(subj => {
      const percentage = subj.percentage || 0;
      const gradeInfo = calculateGrade(percentage);
      
      // Calculate CA and Exam from actual assessment data
      let caMarks = 0;
      let caMaxMarks = 0;
      let examMarks = 0;
      let examMaxMarks = 0;
      
      // Parse assessments to separate CA (quiz, homework, test, project, etc.) from Exam
      if (subj.assessments && subj.assessments.length > 0) {
        subj.assessments.forEach(assessment => {
          const title = (assessment.assessment_title || '').toLowerCase();
          if (title.includes('exam')) {
            examMarks += assessment.marks_obtained || 0;
            examMaxMarks += assessment.max_marks || 0;
          } else {
            // All other assessments are CA (quiz, homework, test, project, exercise, etc.)
            caMarks += assessment.marks_obtained || 0;
            caMaxMarks += assessment.max_marks || 0;
          }
        });
      }
      
      // Calculate CA percentage (out of 40) and Exam percentage (out of 60)
      const caPercentage = caMaxMarks > 0 ? (caMarks / caMaxMarks) * 40 : 0;
      const examPercentage = examMaxMarks > 0 ? (examMarks / examMaxMarks) * 60 : 0;
      const totalMarks = Math.round(caPercentage + examPercentage);
      
      // Recalculate grade based on actual CA+Exam total
      const actualGradeInfo = calculateGrade(totalMarks);
      
      return {
        name: subj.subject?.toUpperCase() || 'SUBJECT',
        ca40: Math.round(caPercentage),
        exam60: Math.round(examPercentage),
        total100: totalMarks,
        rank: subj.ranking ? `${subj.ranking}/${subj.total_students || '?'}` : '-',
        grade: actualGradeInfo.grade,
        gpa: actualGradeInfo.gpa,
        description: actualGradeInfo.description,
        absences: absences,
        assessments: subj.assessments || [],
        // Include raw marks for transparency
        caRawMarks: caMarks,
        caMaxMarks: caMaxMarks,
        examRawMarks: examMarks,
        examMaxMarks: examMaxMarks
      };
    });
    
    // Filter out subjects with no grades (0 CA and 0 Exam marks)
    subjects = subjects.filter(s => s.ca40 > 0 || s.exam60 > 0 || (s.assessments && s.assessments.length > 0 && s.assessments.some(a => a.marks_obtained > 0)));
  } else {
    // Fallback to legacy local state calculation (for backward compatibility)
    const quizMark = parseFloat(getMarks('quiz')) || 0;
    const homeworkMark = parseFloat(getMarks('homework')) || 0;
    const projectMark = parseFloat(getMarks('project')) || 0;
    const classworkMark = parseFloat(getMarks('classwork')) || 0;
    const examMark = parseFloat(getMarks('exam')) || 0;
    
    const caTotal = Math.min(quizMark + homeworkMark + projectMark + classworkMark, 40);
    const examTotal = Math.min(examMark, 60);
    const totalMarks = caTotal + examTotal;
    const gradeInfo = calculateGrade(totalMarks);
    
    subjects = [
      { 
        name: classInfo?.subject?.toUpperCase() || 'SUBJECT', 
        ca40: caTotal, 
        exam60: examTotal, 
        total100: totalMarks,
        rank: '-',
        ...gradeInfo,
        absences: absences
      }
    ];
  }

  // Add auto-generated comments to each subject
  subjects.forEach(subject => {
    subject.teacherComment = generateSubjectComment(
      subject.name.toLowerCase(), 
      subject.total100, 
      subject.grade, 
      studentFirstName
    );
  });

  // Calculate overall GPA (average of all subject GPAs)
  const totalGpa = subjects.length > 0 
    ? subjects.reduce((sum, s) => sum + s.gpa, 0) / subjects.length 
    : 0;
  
  // Get overall grade based on GPA - Official Vanuatu A-E Scale
  const getGradeFromGPA = (gpa) => {
    if (gpa >= 3.5) return { grade: 'A', gpa: 4.0, description: 'Excellent' };
    if (gpa >= 2.5) return { grade: 'B', gpa: 3.0, description: 'Good' };
    if (gpa >= 1.5) return { grade: 'C', gpa: 2.0, description: 'Satisfactory' };
    if (gpa >= 0.5) return { grade: 'D', gpa: 1.0, description: 'Limited' };
    return { grade: 'E', gpa: 0.0, description: 'Low' };
  };
  
  const overallGrade = getGradeFromGPA(totalGpa);

  // Generate auto comments
  const teacherRemarks = generateTeacherRemarks(overallGrade.grade, studentFirstName, absences);
  const principalRemarks = generatePrincipalRemarks(overallGrade.grade, studentFirstName, totalGpa);

  const printReportCard = () => {
    const printWindow = window.open('', '_blank');
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>School Report - ${student.name}</title>
        <style>
          @page {
            size: landscape;
            margin: 8mm;
          }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            padding: 10px; 
            max-width: 100%; 
            margin: 0 auto;
            font-size: 10px;
            line-height: 1.3;
            background: white;
          }
          
          /* Header */
          .header {
            background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
            padding: 8px;
            text-align: center;
            border-radius: 6px;
            margin-bottom: 8px;
          }
          .school-name {
            font-size: 18px;
            font-weight: bold;
            color: #155724;
            margin-bottom: 2px;
          }
          .school-address {
            font-size: 10px;
            color: #155724;
            margin-bottom: 1px;
          }
          .school-phone {
            font-size: 9px;
            color: #155724;
          }
          .report-title {
            background: #155724;
            color: white;
            padding: 6px;
            text-align: center;
            font-size: 13px;
            font-weight: bold;
            margin: 6px 0;
            border-radius: 4px;
          }
          
          /* Student Info */
          .student-info {
            display: flex;
            justify-content: space-between;
            background: #f8f9fa;
            padding: 6px 10px;
            border-radius: 4px;
            margin-bottom: 8px;
            border: 1px solid #dee2e6;
          }
          .student-info-item {
            display: flex;
            gap: 6px;
          }
          .student-info-label {
            font-weight: bold;
            color: #495057;
          }
          .student-info-value {
            color: #212529;
          }
          
          /* Main Grades Table */
          .grades-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 8px;
          }
          .grades-table th {
            background: #155724;
            color: white;
            padding: 6px 4px;
            text-align: center;
            font-size: 9px;
            border: 1px solid #155724;
          }
          .grades-table th:first-child {
            text-align: left;
            width: 70px;
          }
          .grades-table th:last-child {
            text-align: left;
            width: 200px;
          }
          .grades-table td {
            border: 1px solid #dee2e6;
            padding: 5px 4px;
            text-align: center;
            font-size: 10px;
          }
          .grades-table td:first-child {
            text-align: left;
            font-weight: bold;
            background: #f8f9fa;
          }
          .grades-table td:last-child {
            text-align: left;
            font-size: 9px;
            padding: 4px 6px;
          }
          .grades-table tbody tr:hover {
            background: #f1f3f5;
          }
          
          /* GPA Section */
          .gpa-section {
            display: flex;
            gap: 15px;
            margin-bottom: 8px;
          }
          .gpa-table-container {
            flex: 1;
          }
          .gpa-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 10px;
          }
          .gpa-table th {
            background: #e9ecef;
            padding: 4px;
            border: 1px solid #dee2e6;
            font-weight: bold;
          }
          .gpa-table td {
            padding: 2px 4px;
            border: 1px solid #dee2e6;
            text-align: center;
          }
          .gpa-definition {
            font-size: 9px;
            color: #6c757d;
            margin-top: 4px;
            padding: 4px;
            background: #f8f9fa;
            border-radius: 4px;
          }
          .total-gpa {
            background: #d4edda;
            font-weight: bold;
            padding: 4px;
            border-radius: 4px;
            text-align: center;
            margin-top: 4px;
            font-size: 12px;
            color: #155724;
          }
          
          /* Remarks Sections */
          .remarks-section {
            margin-bottom: 6px;
          }
          .remarks-title {
            background: #155724;
            color: white;
            padding: 4px 10px;
            font-weight: bold;
            font-size: 11px;
            border-radius: 4px 4px 0 0;
          }
          .remarks-content {
            border: 1px solid #dee2e6;
            border-top: none;
            padding: 6px 10px;
            min-height: 40px;
            font-size: 10px;
            line-height: 1.4;
            background: #fff;
            border-radius: 0 0 4px 4px;
          }
          .remarks-signature {
            text-align: right;
            margin-top: 8px;
            font-style: italic;
            color: #6c757d;
          }
          
          /* Signatures */
          .signatures {
            display: flex;
            justify-content: space-between;
            margin-top: 10px;
            padding-top: 8px;
          }
          .signature-box {
            text-align: center;
            width: 180px;
          }
          .signature-line {
            border-top: 1px solid #212529;
            margin-top: 20px;
            padding-top: 4px;
            font-size: 10px;
            font-weight: bold;
          }
          
          /* Footer */
          .footer {
            text-align: center;
            margin-top: 8px;
            padding-top: 6px;
            border-top: 1px solid #dee2e6;
            color: #6c757d;
            font-size: 8px;
          }
          
          /* Grade Colors */
          .grade-aplus { background: #d4edda; color: #155724; font-weight: bold; }
          .grade-a { background: #d1ecf1; color: #0c5460; font-weight: bold; }
          .grade-bplus { background: #cce5ff; color: #004085; }
          .grade-b { background: #e2e3e5; color: #383d41; }
          .grade-cplus { background: #fff3cd; color: #856404; }
          .grade-c { background: #ffeeba; color: #856404; }
          .grade-d { background: #f8d7da; color: #721c24; }
          .grade-e { background: #f5c6cb; color: #721c24; }
          
          @media print {
            @page {
              size: landscape;
              margin: 5mm;
            }
            body { 
              padding: 5px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .no-print { display: none; }
            * { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <!-- Header -->
        <div class="header" style="display: flex; align-items: center; justify-content: center; gap: 15px;">
          ${logoUrl ? `<img src="${logoUrl}" alt="School Logo" style="height: 60px; width: auto;">` : ''}
          <div style="text-align: center;">
            <div class="school-name">${schoolName}</div>
            <div class="school-address">${schoolAddress}</div>
            <div class="school-phone">${schoolName} Tel:${schoolPhone}</div>
          </div>
          ${logoUrl ? `<img src="${logoUrl}" alt="School Logo" style="height: 60px; width: auto;">` : ''}
        </div>
        
        <!-- Report Title -->
        <div class="report-title">END OF ${term.toUpperCase()} SCHOOL REPORT ${year}</div>
        
        <!-- Student Info -->
        <div class="student-info">
          <div class="student-info-item">
            <span class="student-info-label">Student Name:</span>
            <span class="student-info-value">${student.name}</span>
          </div>
          <div class="student-info-item">
            <span class="student-info-label">Student ID:</span>
            <span class="student-info-value">${student.birth_registration || student.student_id || 'N/A'}</span>
          </div>
          <div class="student-info-item">
            <span class="student-info-label">Class:</span>
            <span class="student-info-value">${classInfo?.name || 'Year ' + (classInfo?.year || '1')}</span>
          </div>
        </div>
        
        <!-- Grades Table -->
        <table class="grades-table">
          <thead>
            <tr>
              <th>Subject</th>
              <th>40%</th>
              <th>60%</th>
              <th>100%</th>
              <th>Rank</th>
              <th>Grade</th>
              <th>Absences</th>
              <th>Teachers Comments</th>
            </tr>
          </thead>
          <tbody>
            ${subjects.map(subject => {
              const gradeClass = subject.grade.replace('+', 'plus').toLowerCase();
              return `
                <tr>
                  <td>${subject.name}</td>
                  <td>${Math.min(subject.ca40, 40)}</td>
                  <td>${Math.min(subject.exam60, 60)}</td>
                  <td><strong>${Math.min(subject.total100, 100)}</strong></td>
                  <td>${subject.rank}</td>
                  <td class="grade-${gradeClass}">${subject.grade}</td>
                  <td>${subject.absences}</td>
                  <td>${subject.teacherComment}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
        
        <!-- GPA Section -->
        <div class="gpa-section">
          <div class="gpa-table-container">
            <table class="gpa-table">
              <thead>
                <tr>
                  <th>GPA</th>
                  <th>Value</th>
                  <th>Total GPA</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>A</td><td>4.0</td><td rowspan="5" style="vertical-align: middle; font-size: 16px; font-weight: bold; color: #155724;">${totalGpa.toFixed(1)}</td></tr>
                <tr><td>B</td><td>3.0</td></tr>
                <tr><td>C</td><td>2.0</td></tr>
                <tr><td>D</td><td>1.0</td></tr>
                <tr><td>E</td><td>0.0</td></tr>
              </tbody>
            </table>
            <div class="gpa-definition">
              <strong>GPA</strong> = Grade Point Average. It is calculated by taking the average of all grade point values earned across all subjects.
            </div>
          </div>
          
          <div class="gpa-table-container">
            <table class="gpa-table">
              <thead>
                <tr>
                  <th>Grade</th>
                  <th>Range</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr><td class="grade-a">A</td><td>85-100%</td><td>Excellent</td></tr>
                <tr><td class="grade-b">B</td><td>70-84%</td><td>Good</td></tr>
                <tr><td class="grade-c">C</td><td>50-69%</td><td>Satisfactory</td></tr>
                <tr><td class="grade-d">D</td><td>30-49%</td><td>Limited</td></tr>
                <tr><td class="grade-e">E</td><td>1-29%</td><td>Low</td></tr>
              </tbody>
            </table>
            <div class="total-gpa">
              Overall Performance: <span style="font-size: 14px;">${overallGrade.grade}</span> - ${overallGrade.description}
            </div>
          </div>
        </div>
        
        <!-- Teachers Remarks -->
        <div class="remarks-section">
          <div class="remarks-title">Teachers Remarks</div>
          <div class="remarks-content">
            ${teacherRemarks}
          </div>
        </div>
        
        <!-- Principal Remarks -->
        <div class="remarks-section">
          <div class="remarks-title">Principal Remarks</div>
          <div class="remarks-content">
            ${principalRemarks}
          </div>
        </div>
        
        <!-- Signatures - Only Principal -->
        <div class="signatures" style="justify-content: center;">
          <div class="signature-box" style="text-align: center;">
            ${isQuatui ? '<img src="/quatui-principal-signature.png" alt="Principal Signature" style="height: 45px; width: auto; margin: 0 auto -5px auto; display: block;">' : ''}${isLoquirutaro ? '<img src="/loquirutaro-principal-signature.png" alt="Principal Signature" style="height: 45px; width: auto; margin: 0 auto -5px auto; display: block;">' : ''}
            <div style="border-top: 2px solid #212529; padding-top: 3px; margin-top: 0;">
              <div style="font-size: 9px; color: #6c757d;">Principal</div>
            </div>
          </div>
        </div>
        
        <div class="footer">
          <p>This report card was generated by Penama Education Portal</p>
          <p>Date Issued: ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  // Grade color helper
  const getGradeColorClass = (grade) => {
    // Color classes for Official Vanuatu A-E Scale
    switch(grade) {
      case 'A': return 'bg-green-100 text-green-800 border-green-300';
      case 'B': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'C': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'D': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'E': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="primary-report-card-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="w-6 h-6 text-green-600" />
            Primary School Report Card
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Student term report with grades and performance summary
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* School Header */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200 text-center">
            <div className="flex items-center justify-center gap-4">
              {logoUrl && <img src={logoUrl} alt="School Logo" className="h-16 w-auto" />}
              <div>
                <h2 className="text-xl font-bold text-green-800">{schoolName}</h2>
                <p className="text-green-700 text-sm">{schoolAddress}</p>
                <p className="text-green-600 text-xs">{schoolName} Tel:{schoolPhone}</p>
              </div>
              {logoUrl && <img src={logoUrl} alt="School Logo" className="h-16 w-auto" />}
            </div>
            <div className="mt-2 bg-green-700 text-white py-2 px-4 rounded font-bold">
              END OF {term.toUpperCase()} SCHOOL REPORT {year}
            </div>
          </div>

          {/* Student Info */}
          <div className="bg-gray-50 p-4 rounded-lg border flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-xs text-gray-500">Student Name</p>
                <p className="font-semibold">{student.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-xs text-gray-500">Class</p>
                <p className="font-semibold">{classInfo?.name || `Year ${classInfo?.year}`}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <School className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-xs text-gray-500">Student ID</p>
                <p className="font-semibold">{student.birth_registration || student.student_id || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-xs text-gray-500">Term</p>
                <p className="font-semibold">{term}</p>
              </div>
            </div>
          </div>

          {/* Grades Table */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-green-700 text-white">
                <tr>
                  <th className="text-left p-3 w-24">Subject</th>
                  <th className="text-center p-3 w-16">40%</th>
                  <th className="text-center p-3 w-16">60%</th>
                  <th className="text-center p-3 w-16">100%</th>
                  <th className="text-center p-3 w-14">Rank</th>
                  <th className="text-center p-3 w-16">Grade</th>
                  <th className="text-center p-3 w-16">Absences</th>
                  <th className="text-left p-3">Teachers Comments</th>
                </tr>
              </thead>
              <tbody>
                {subjects.map((subject, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-bold bg-gray-50">{subject.name}</td>
                    <td className="text-center p-3">{Math.min(subject.ca40, 40)}</td>
                    <td className="text-center p-3">{Math.min(subject.exam60, 60)}</td>
                    <td className="text-center p-3 font-bold">{Math.min(subject.total100, 100)}</td>
                    <td className="text-center p-3">{subject.rank}</td>
                    <td className="text-center p-3">
                      <span className={`px-2 py-1 rounded text-xs font-bold border ${getGradeColorClass(subject.grade)}`}>
                        {subject.grade}
                      </span>
                    </td>
                    <td className="text-center p-3">{subject.absences}</td>
                    <td className="p-3 text-xs text-gray-600">{subject.teacherComment}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* GPA and Performance Rating */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* GPA Scale */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-100 p-2 font-bold text-sm text-center">GPA Scale</div>
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 border">GPA</th>
                    <th className="p-2 border">Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="p-1 border text-center">A</td><td className="p-1 border text-center">4.0</td></tr>
                  <tr><td className="p-1 border text-center">B</td><td className="p-1 border text-center">3.0</td></tr>
                  <tr><td className="p-1 border text-center">C</td><td className="p-1 border text-center">2.0</td></tr>
                  <tr><td className="p-1 border text-center">D</td><td className="p-1 border text-center">1.0</td></tr>
                  <tr><td className="p-1 border text-center">E</td><td className="p-1 border text-center">0.0</td></tr>
                </tbody>
              </table>
              <div className="bg-green-100 p-3 text-center">
                <span className="text-sm font-bold text-green-800">Total GPA: {totalGpa.toFixed(1)}</span>
              </div>
            </div>

            {/* Performance Rating - Official Vanuatu A-E Scale */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-100 p-2 font-bold text-sm text-center">Performance Rating</div>
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 border">Grade</th>
                    <th className="p-2 border">Range</th>
                    <th className="p-2 border">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className={`p-1 border text-center ${getGradeColorClass('A')}`}>A</td><td className="p-1 border text-center">85-100%</td><td className="p-1 border">Excellent</td></tr>
                  <tr><td className={`p-1 border text-center ${getGradeColorClass('B')}`}>B</td><td className="p-1 border text-center">70-84%</td><td className="p-1 border">Good</td></tr>
                  <tr><td className={`p-1 border text-center ${getGradeColorClass('C')}`}>C</td><td className="p-1 border text-center">50-69%</td><td className="p-1 border">Satisfactory</td></tr>
                  <tr><td className={`p-1 border text-center ${getGradeColorClass('D')}`}>D</td><td className="p-1 border text-center">30-49%</td><td className="p-1 border">Limited</td></tr>
                  <tr><td className={`p-1 border text-center ${getGradeColorClass('E')}`}>E</td><td className="p-1 border text-center">1-29%</td><td className="p-1 border">Low</td></tr>
                </tbody>
              </table>
              <div className={`p-3 text-center ${getGradeColorClass(overallGrade.grade)}`}>
                <span className="text-sm font-bold">Overall: {overallGrade.grade} - {overallGrade.description}</span>
              </div>
            </div>
          </div>

          {/* Teacher Remarks - Auto Generated */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-green-700 text-white p-2 font-bold text-sm">Teachers Remarks</div>
            <div className="p-4 text-sm text-gray-700 leading-relaxed">
              {teacherRemarks}
            </div>
          </div>

          {/* Principal Remarks - Auto Generated */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-green-700 text-white p-2 font-bold text-sm">Principal Remarks</div>
            <div className="p-4 text-sm text-gray-700 leading-relaxed">
              {principalRemarks}
            </div>
            {/* Principal Signature */}
            <div className="flex justify-end px-6 pb-4">
              <div className="flex flex-col items-center">
                {isQuatui && (
                  <img src="/quatui-principal-signature.png" alt="Principal Signature" style={{height: '45px', width: 'auto', marginBottom: '-5px'}} />
                )}
                {isLoquirutaro && (
                  <img src="/loquirutaro-principal-signature.png" alt="Principal Signature" style={{height: '45px', width: 'auto', marginBottom: '-5px'}} />
                )}
                <div className="border-t-2 border-gray-800 mt-0 pt-1 px-6 text-center">
                  <p className="text-xs text-gray-500">Principal</p>
                </div>
              </div>
            </div>
          </div>

          {/* Overall Summary */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Award className={`w-10 h-10 ${
                  overallGrade.grade.startsWith('A') ? 'text-green-600' :
                  overallGrade.grade.startsWith('B') ? 'text-blue-600' :
                  overallGrade.grade.startsWith('C') ? 'text-yellow-600' :
                  'text-red-600'
                }`} />
                <div>
                  <p className="text-sm text-gray-600">Overall Performance</p>
                  <p className="text-xl font-bold text-green-800">{overallGrade.description}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-green-700">{totalGpa.toFixed(1)}</p>
                <p className="text-sm text-gray-500">Total GPA</p>
              </div>
            </div>
          </div>

          {/* Print Button */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose} data-testid="close-report-btn">
              <X className="w-4 h-4 mr-2" />
              Close
            </Button>
            <Button onClick={printReportCard} className="bg-green-600 hover:bg-green-700" data-testid="print-report-btn">
              <Printer className="w-4 h-4 mr-2" />
              Print Report Card
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
